import { useState, useRef, useEffect } from "react";
import { Textarea } from "../ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Code,
  Code2,
  Smile,
  AtSign,
} from "lucide-react";

const EMOJI_SET = [
  "👍","❤️","🎉","🚀","✅","👀","🙌","🔥","😀","😂","😊","😍",
  "🤔","😅","🙏","👏","💯","👌","🤝","👋","😎","😢","😡","🥳",
  "💪","🎯","💡","⭐","☕","🍕","🐛","📝",
];

/**
 * Textarea with a lightweight @-mention autocomplete driven by a users list.
 * When `showToolbar` is true, renders a Slack-style rich-text formatting
 * toolbar above the textarea (Bold/Italic/Underline/Strike/Link/Lists/Quote/
 * Code/Emoji/@). All formatting is emitted as markdown so ReactMarkdown can
 * render it back on read.
 */
export default function MentionTextarea({
  value,
  onChange,
  users,
  placeholder,
  rows = 2,
  className = "",
  "data-testid": testId,
  onKeyDown,
  showToolbar = false,
}) {
  const ref = useRef(null);
  const [openAt, setOpenAt] = useState(null); // index where @ begins
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const handleChange = (e) => {
    const val = e.target.value;
    const caret = e.target.selectionStart || 0;
    onChange(val);
    // Look backwards for @ — allow at most ONE space inside the query so
    // full-name typing ("@mehmet d…") keeps the dropdown open.
    let i = caret - 1;
    let found = null;
    let spaces = 0;
    while (i >= 0) {
      const ch = val[i];
      if (ch === "@") {
        if (i === 0 || /\s/.test(val[i - 1])) {
          found = i;
        }
        break;
      }
      if (ch === "\n") break;
      if (ch === " ") {
        spaces++;
        if (spaces > 1) break;
      }
      i--;
    }
    if (found !== null) {
      const q = val.slice(found + 1, caret).toLowerCase();
      if (q.length <= 40) {
        setOpenAt(found);
        setQuery(q);
        setHighlight(0);
        return;
      }
    }
    setOpenAt(null);
    setQuery("");
  };

  const suggestions =
    openAt === null
      ? []
      : users
          .filter((u) => u.active !== false)
          .filter((u) => {
            // Normalize "_"/"." to spaces so "@mehmet_demir" matches too.
            const q = query.replace(/[_.]/g, " ");
            if (!q.trim()) return true;
            const name = (u.name || "").toLowerCase();
            const email = (u.email || "").toLowerCase();
            const localpart = email.split("@")[0] || "";
            // Full-name prefix (works with spaces: "mehmet d" → Mehmet Demir)
            if (name.startsWith(q) || localpart.startsWith(query)) return true;
            if (!q.includes(" ")) {
              for (const part of name.split(/\s+/)) {
                if (part.startsWith(q)) return true;
              }
            }
            return false;
          })
          .sort((a, b) =>
            (a.name || "").toLocaleLowerCase("tr").localeCompare(
              (b.name || "").toLocaleLowerCase("tr"),
              "tr",
            ),
          );

  const insertMention = (u) => {
    const el = ref.current;
    if (!el) return;
    const caret = el.selectionStart || 0;
    const before = value.slice(0, openAt);
    const after = value.slice(caret);
    const token = u.email.split("@", 1)[0];
    const next = `${before}@${token} ${after}`;
    onChange(next);
    setOpenAt(null);
    setQuery("");
    setTimeout(() => {
      const pos = before.length + token.length + 2;
      el.focus();
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (openAt !== null && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlight((h) => (h + 1) % suggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(suggestions[highlight]);
        return;
      }
      if (e.key === "Escape") {
        setOpenAt(null);
        return;
      }
    }
    if (onKeyDown) onKeyDown(e);
  };

  /**
   * Wrap the current selection (or insert placeholder) with markdown markers.
   * `linePrefix=true` prepends `prefix` to the start of the current line(s)
   * instead of wrapping the selection (useful for lists / quotes).
   */
  const applyFormat = (prefix, suffix = "", { linePrefix = false, placeholder = "" } = {}) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const before = value.slice(0, start);
    const selected = value.slice(start, end) || placeholder;
    const after = value.slice(end);
    let next;
    let caretEnd;
    if (linePrefix) {
      const lineStart = before.lastIndexOf("\n") + 1;
      const head = value.slice(0, lineStart);
      const lineAndSelected = value.slice(lineStart, end) || placeholder;
      // Prefix every line
      const prefixed = lineAndSelected
        .split("\n")
        .map((l) => `${prefix}${l}`)
        .join("\n");
      next = `${head}${prefixed}${after}`;
      caretEnd = head.length + prefixed.length;
    } else {
      next = `${before}${prefix}${selected}${suffix}${after}`;
      caretEnd = before.length + prefix.length + selected.length;
    }
    onChange(next);
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(caretEnd, caretEnd);
    }, 0);
  };

  const insertAtCursor = (text) => applyFormat(text, "");
  const insertEmoji = (e) => applyFormat(e, "");
  const insertLink = () => {
    const url = window.prompt("Bağlantı URL'si:");
    if (!url) return;
    applyFormat("[", `](${url})`, { placeholder: "bağlantı metni" });
  };

  return (
    <div className="relative flex-1">
      {showToolbar && (
        <ToolbarBar
          onFormat={applyFormat}
          onInsertEmoji={insertEmoji}
          onInsertLink={insertLink}
          onInsertMention={() => insertAtCursor("@")}
        />
      )}
      <Textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        className={[
          showToolbar
            ? "rounded-t-none border-t-0 focus-visible:border-t-0"
            : "",
          className,
        ].join(" ").trim()}
        data-testid={testId}
      />
      {openAt !== null && suggestions.length > 0 && (
        <ul
          data-testid="mention-suggestions"
          className="absolute z-10 mt-1 max-h-48 w-64 overflow-y-auto rounded-md border border-zinc-200 bg-white p-1 shadow-lg"
        >
          {suggestions.map((u, i) => (
            <li
              key={u.id}
              onMouseDown={(e) => {
                e.preventDefault();
                insertMention(u);
              }}
              className={[
                "cursor-pointer rounded px-2 py-1.5 text-sm",
                i === highlight ? "bg-zinc-900 text-white" : "text-zinc-700",
              ].join(" ")}
            >
              <span className="font-medium">{u.name}</span>{" "}
              <span
                className={
                  i === highlight ? "text-zinc-200" : "text-zinc-500"
                }
              >
                @{u.email.split("@")[0]}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ToolbarBtn({ onClick, title, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testId}
      className="rounded p-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
    >
      {children}
    </button>
  );
}

function ToolbarBar({ onFormat, onInsertEmoji, onInsertLink, onInsertMention }) {
  return (
    <div
      data-testid="comment-toolbar"
      className="flex flex-wrap items-center gap-0.5 rounded-t-md border border-b-0 border-zinc-200 bg-zinc-50/70 px-2 py-1"
    >
      <ToolbarBtn
        onClick={() => onFormat("**", "**", { placeholder: "kalın metin" })}
        title="Kalın · Ctrl+B"
        testId="fmt-bold"
      >
        <Bold className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => onFormat("*", "*", { placeholder: "italik metin" })}
        title="İtalik · Ctrl+I"
        testId="fmt-italic"
      >
        <Italic className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => onFormat("<u>", "</u>", { placeholder: "altı çizili" })}
        title="Altı çizili"
        testId="fmt-underline"
      >
        <Underline className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => onFormat("~~", "~~", { placeholder: "üstü çizili" })}
        title="Üstü çizili"
        testId="fmt-strike"
      >
        <Strikethrough className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <span className="mx-1 h-4 w-px bg-zinc-200" />
      <ToolbarBtn onClick={onInsertLink} title="Bağlantı ekle" testId="fmt-link">
        <LinkIcon className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => onFormat("- ", "", { linePrefix: true, placeholder: "madde" })}
        title="Madde listesi"
        testId="fmt-ul"
      >
        <List className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => onFormat("1. ", "", { linePrefix: true, placeholder: "madde" })}
        title="Numaralı liste"
        testId="fmt-ol"
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => onFormat("> ", "", { linePrefix: true, placeholder: "alıntı" })}
        title="Alıntı"
        testId="fmt-quote"
      >
        <Quote className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() => onFormat("`", "`", { placeholder: "kod" })}
        title="Satır içi kod"
        testId="fmt-code"
      >
        <Code className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <ToolbarBtn
        onClick={() =>
          onFormat("\n```\n", "\n```\n", { placeholder: "kod bloğu" })
        }
        title="Kod bloğu"
        testId="fmt-codeblock"
      >
        <Code2 className="h-3.5 w-3.5" />
      </ToolbarBtn>
      <span className="mx-1 h-4 w-px bg-zinc-200" />
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            title="Emoji ekle"
            data-testid="fmt-emoji"
            className="rounded p-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
          >
            <Smile className="h-3.5 w-3.5" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-2">
          <div className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">
            Emoji
          </div>
          <div className="grid grid-cols-8 gap-1">
            {EMOJI_SET.map((emo) => (
              <button
                key={emo}
                type="button"
                onClick={() => onInsertEmoji(emo)}
                data-testid={`emoji-${emo}`}
                className="rounded p-1 text-base hover:bg-zinc-100"
              >
                {emo}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <ToolbarBtn onClick={onInsertMention} title="@ Kişi etiketle" testId="fmt-mention">
        <AtSign className="h-3.5 w-3.5" />
      </ToolbarBtn>
    </div>
  );
}
